import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import Text from "@/components/ui/text";
import { useSaveCurrentView } from "@/entities/views/hooks";
import { ViewDataConfiguration, ViewType } from "@/entities/views/types";
import { sortStateToQuery } from "@/entities/views/helpers";
import {
  deleteTempView,
  getColumnsToSave,
  getTempView
} from "@/entities/dataTables/helpers";
import { ColumnData } from "@/entities/dataTables/types";
import { SortingState } from "@tanstack/react-table";
import { Filters } from "@/services/api/types";
import toast from "react-hot-toast";
import IconContainer from "@/components/ui/icon-container";
import { Add, CircleArrowDown } from "@dreamhub/designsystem/icons";
import NewNameViewModal from "@/components/layouts/NameViewModal/NewNameViewModal";

interface Props {
  selectedView: ViewDataConfiguration;
  views: ViewDataConfiguration[];
  setAndSaveViews: (
    newViews: ViewDataConfiguration[],
    oldViews: ViewDataConfiguration[]
  ) => Promise<void>;
  filters: Filters;
  sorting?: SortingState;
  defaultSortByValue?: string;
  groupBy: string[];
  searchTerm: string;
  columnsState: ColumnData[];
  viewType?: ViewType;
  activateView?: (newView: ViewDataConfiguration) => void;
}

const NewViewActions: React.FC<Props> = ({
  selectedView,
  views,
  setAndSaveViews,
  filters,
  sorting = [],
  defaultSortByValue = "",
  groupBy,
  searchTerm,
  columnsState,
  viewType = ViewType.DEALS_TABLE,
  activateView
}) => {
  const { t } = useTranslation();
  const { saveView, isLoading: isSaving } = useSaveCurrentView();
  const [nameViewModalOpen, setNameViewModalOpen] = useState(false);

  const handleSaveView = async () => {
    try {
      const tempView = getTempView(selectedView);
      const sortingQuery = tempView?.config?.sort
        ? tempView.config.sort
        : sortStateToQuery(sorting, defaultSortByValue);
      const formattedConfig = {
        ...(tempView?.config || { filters, groupBy, searchTerm }),
        columns: getColumnsToSave(selectedView, columnsState)
      };
      await saveView(selectedView, {
        ...formattedConfig,
        sortBy: sortingQuery
      });
      const updatedView = {
        ...selectedView,
        isOpen: true,
        config: {
          ...formattedConfig,
          sort: sortingQuery
        }
      };
      const updatedViews = views.map((view) =>
        view.id === selectedView.id ? updatedView : view
      );
      setAndSaveViews(updatedViews, views);
      deleteTempView(selectedView);
      if (activateView) {
        activateView(updatedView);
      }
      toast.success(
        t("datatoolbar.views.saveSuccess", { viewName: selectedView?.name })
      );
    } catch (err) {
      console.error("Error saving view:", err);
      toast.error(t("datatoolbar.views.errorSaving"));
    }
  };

  return (
    <>
      {selectedView.isSystem ? (
        <Button
          variant="secondary"
          className="py-[3.5px]"
          onClick={() => setNameViewModalOpen(true)}
        >
          {t("datatoolbar.views.saveView")}
        </Button>
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              className="py-[3.5px]"
              disabled={isSaving}
            >
              {t("datatoolbar.views.save")}
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            padding={4}
            className="flex flex-col gap-2 w-fit"
            align="end"
            sideOffset={4}
          >
            <Command>
              <CommandList>
                <CommandGroup>
                  <CommandItem onSelect={handleSaveView}>
                    <IconContainer size="5" color="secondary">
                      <CircleArrowDown />
                    </IconContainer>
                    <Text variant="body">
                      {t("datatoolbar.views.saveToThisView")}
                    </Text>
                  </CommandItem>
                  <CommandItem onSelect={() => setNameViewModalOpen(true)}>
                    <IconContainer size="5" color="secondary">
                      <Add />
                    </IconContainer>
                    <Text variant="body">
                      {t("datatoolbar.views.saveAsNew")}
                    </Text>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
      <NewNameViewModal
        isOpen={nameViewModalOpen}
        setIsOpen={setNameViewModalOpen}
        viewType={viewType}
        filters={filters}
        sortBy={sortStateToQuery(sorting, defaultSortByValue)}
        groupBy={groupBy}
        searchTerm={searchTerm}
        columnsState={columnsState}
        onSuccess={(newView) => {
          const updatedViews = [
            ...views,
            {
              ...newView,
              isOpen: true
            }
          ];
          setAndSaveViews(updatedViews, views);
          activateView?.(newView);
          setNameViewModalOpen(false);
        }}
        selectedView={selectedView}
      />
    </>
  );
};

export default NewViewActions;
